import { ExtractAtomicOperationFromDefinition } from "../atomic-operations/AtomicOperationDefinition.js";
import { DateTimeString } from "../dateTypes.js";
import { GridColumn } from "../grid-layout/gridTypes.js";
import { GridLayoutId, GridRowId } from "../idTypeBrands.js";
import { SemanticCap } from "../semanticCaps.js";

import { createHexVersionAtomicOperationDefinition } from "./HexVersionAtomicOperationDefinition.js";

export interface GridRowUpdatableFields {
  gridColumns: readonly GridColumn[];
  gridLayoutId: GridLayoutId;
  order: string;
}

const _UpdateGridRowSafeFields: {
  [key in keyof GridRowUpdatableFields]: "";
} = {
  gridColumns: "",
  gridLayoutId: "",
  order: "",
};
export const UpdateGridRowSafeFields = Object.keys(_UpdateGridRowSafeFields);

const UPDATE_GRID_ROW_TYPE = "UPDATE_GRID_ROW" as const;
export const UPDATE_GRID_ROW = createHexVersionAtomicOperationDefinition({
  type: UPDATE_GRID_ROW_TYPE,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
  },
  writeAuth: {
    kind: "hasSemanticCapOnIdArg",
    idArg: "gridRowId",
    idType: "GridRow",
    cap: SemanticCap.EDIT_PROJECT_CONTENTS,
  },
  logSafe: ["gridRowId", "key"],
  conflictId: (op) =>
    `${UPDATE_GRID_ROW_TYPE}-${op.payload.gridRowId}-${op.payload.key}`,
  creationId: (op) => `${CREATE_GRID_ROW.type}-${op.payload.gridRowId}`,
  create: <K extends keyof GridRowUpdatableFields>(
    gridRowId: GridRowId,
    key: K,
    value: GridRowUpdatableFields[K],
  ) => ({
    type: UPDATE_GRID_ROW_TYPE,
    payload: {
      gridRowId,
      key,
      value,
    },
  }),
});

export type UPDATE_GRID_ROW = ExtractAtomicOperationFromDefinition<
  typeof UPDATE_GRID_ROW
>;

const CREATE_GRID_ROW_TYPE = "CREATE_GRID_ROW" as const;
export const CREATE_GRID_ROW = createHexVersionAtomicOperationDefinition({
  type: CREATE_GRID_ROW_TYPE,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
  },
  writeAuth: {
    kind: "and",
    and: [
      {
        kind: "hasSemanticCapOnIdArg",
        idArg: "gridLayoutId",
        idType: "GridLayout",
        cap: SemanticCap.EDIT_PROJECT_CONTENTS,
      },
      {
        kind: "idArgDoesNotExist",
        idArg: "gridRowId",
        idType: "GridRow",
      },
    ],
  },
  logSafe: ["gridLayoutId", "gridRowId"],
  conflictId: (op) => `${CREATE_GRID_ROW_TYPE}-${op.payload.gridRowId}`,
  create: ({
    gridColumns,
    gridLayoutId,
    gridRowId,
    insertAt,
  }: {
    gridColumns: readonly GridColumn[];
    gridLayoutId: GridLayoutId;
    gridRowId: GridRowId;
    insertAt: string;
  }) => ({
    type: CREATE_GRID_ROW_TYPE,
    payload: {
      gridColumns,
      gridLayoutId,
      gridRowId,
      insertAt,
    },
  }),
});

export type CREATE_GRID_ROW = ExtractAtomicOperationFromDefinition<
  typeof CREATE_GRID_ROW
>;

const DELETE_GRID_ROW_TYPE = "DELETE_GRID_ROW" as const;
export const DELETE_GRID_ROW = createHexVersionAtomicOperationDefinition({
  type: DELETE_GRID_ROW_TYPE,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
  },
  writeAuth: {
    kind: "hasSemanticCapOnIdArg",
    idArg: "gridRowId",
    idType: "GridRow",
    cap: SemanticCap.EDIT_PROJECT_CONTENTS,
  },
  logSafe: ["gridRowId"],
  conflictId: (op) =>
    `${DELETE_GRID_ROW_TYPE}-${RESTORE_GRID_ROW.type}-${op.payload.gridRowId}`,
  creationId: (op) => `${CREATE_GRID_ROW.type}-${op.payload.gridRowId}`,
  create: (gridRowId: GridRowId) => ({
    type: DELETE_GRID_ROW_TYPE,
    payload: {
      gridRowId,
    },
  }),
});

export type DELETE_GRID_ROW = ExtractAtomicOperationFromDefinition<
  typeof DELETE_GRID_ROW
>;

const RESTORE_GRID_ROW_TYPE = "RESTORE_GRID_ROW" as const;
export const RESTORE_GRID_ROW = createHexVersionAtomicOperationDefinition({
  type: RESTORE_GRID_ROW_TYPE,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
  },
  writeAuth: {
    kind: "hasSemanticCapOnIdArg",
    idArg: "gridRowId",
    idType: "GridRow",
    cap: SemanticCap.EDIT_PROJECT_CONTENTS,
  },
  logSafe: ["gridRowId"],
  conflictId: (op) =>
    `${DELETE_GRID_ROW_TYPE}-${RESTORE_GRID_ROW_TYPE}-${op.payload.gridRowId}`,
  creationId: (op) => `${CREATE_GRID_ROW.type}-${op.payload.gridRowId}`,
  create: (
    gridRowId: GridRowId,
    // the server adds this optional argument
    // in case a new client joins that
    // can't restore using only the GridRowId
    gridRowRestoreInfo: {
      createdDate: DateTimeString;
      updatedDate: DateTimeString;
      revision: number;
      gridLayoutId: GridLayoutId;
      gridColumns: GridColumn[];
      order: string;
    } | null = null,
  ) => ({
    type: RESTORE_GRID_ROW_TYPE,
    payload: {
      gridRowId,
      gridRowRestoreInfo,
    },
  }),
});

export type RESTORE_GRID_ROW = ExtractAtomicOperationFromDefinition<
  typeof RESTORE_GRID_ROW
>;
